import { onBeforeUnmount, toValue, watch } from "vue";
import { useEventListener } from "@vueuse/core";
import { dispatchTranscriptRendered } from "@/common/helpers/custom-events";
import { normalizeExcerptSelection } from "@/common/helpers/excerpt-selection";
import { isChatSourced, transcriptIndexOf } from "@/common/helpers/transcript";
// Ramius excerpt-engine token/attr coupling, shared across transcript experiences and kept out of the
// transcription-layout components. The create/delete bridging a realtime-less experience needs lives
// separately in useRamiusExcerptBridge.
export const useRamiusExcerpts = (options) => {
    const isExcerptable = (segment) => options.isSegmentExcerptable?.(segment) ?? true;
    // Must match the legacy message_stream container + server Excerpt.getLabel so capture() aligns.
    // Per-segment, never per-message: one speaker group can mix ChatMessage bodies and spoken rows.
    const focusAreaIdFor = (segment) => {
        const prefix = toValue(options.focusAreaPrefix);
        return isChatSourced(segment) ? `${prefix}-m-${segment.id}` : `${prefix}-t-${transcriptIndexOf(segment)}`;
    };
    // getTranscriptObject emits "</t> " per word, so word N is fragment N*2; no caption index (external content) falls back to the word position.
    const tokenIdx = (chunk, wordIndex) => (chunk.index ?? wordIndex) * 2;
    // Spread onto the segment text container so capture() resolves the focus area.
    const segmentTextAttrs = (segment) => {
        if (!isExcerptable(segment))
            return {};
        return { id: focusAreaIdFor(segment), class: "rc-excerpt-area" };
    };
    // The legacy marker rail measures the rendered rows, so a filter change leaves it stale. The host
    // owns the rescan (meeting-page.js), so signal it rather than reaching into window.Ramius.
    const notifyRendered = () => {
        if (!toValue(options.enabled))
            return;
        dispatchTranscriptRendered();
    };
    // Only closes a popover an earlier selection left open; the mouseup below is what keeps the engine off editable words.
    const onDblclick = (event) => {
        if (!toValue(options.enabled))
            return;
        const target = event.target;
        if (!target?.closest('[data-editable="true"]'))
            return;
        const confirmId = window.Ramius?.highlight?.confirmId;
        const popover = confirmId ? document.getElementById(confirmId) : null;
        if (popover)
            popover.style.display = "none";
    };
    // Capture phase on document, so it lands before the engine's own bubbling mouseup reads the range.
    useEventListener(document, "mouseup", (event) => {
        if (!toValue(options.enabled))
            return;
        // 2nd click of a dblclick on an editable word: leave the engine no range, so it shows no popover and never arms
        // `Ramius.highlight.handleKeyDown` -- the `s` shortcut that would otherwise swallow the first keystroke typed
        // into the editor.
        const target = event.target;
        if (event.detail >= 2 && target?.closest('[data-editable="true"]')) {
            window.getSelection()?.removeAllRanges();
            return;
        }
        normalizeExcerptSelection(window.getSelection());
    }, { capture: true });
    // The engine re-reads the live selection inside capture(true), and its `S` save shortcut fires on keydown
    // with no intervening mouseup, so a keyboard-adjusted selection (Shift+Home/End, Cmd+Shift+Arrow -- none of
    // which the word handler preventDefaults) would commit unsnapped. Gated on the key because normalizing on
    // every keydown would fight an adjustment still in progress.
    useEventListener(document, "keydown", (event) => {
        const isSaveShortcut = event.key === "s" || event.key === "S" || event.code === "KeyS" || event.keyCode === 83;
        if (!isSaveShortcut)
            return;
        if (toValue(options.enabled))
            normalizeExcerptSelection(window.getSelection());
    }, { capture: true });
    // Watch (not onMounted) so the listener follows the element across conditional-layout remounts.
    watch(options.container, (element, previous) => {
        previous?.removeEventListener("dblclick", onDblclick);
        element?.addEventListener("dblclick", onDblclick);
    }, { immediate: true, flush: "post" });
    onBeforeUnmount(() => options.container.value?.removeEventListener("dblclick", onDblclick));
    return { focusAreaIdFor, tokenIdx, segmentTextAttrs, notifyRendered };
};
